import {noop} from '@benchling/js-utils';
import {SegmentedControl, Stack, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import $ from 'jquery';
import React, {useCallback, useEffect, useState, useRef} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ConnectionFilesTable, {
  getFiltersFromConnectionIds,
  useConnectionIdsAndFiltersState,
} from 'instruments/connection-files/ConnectionFilesTable';
import notifications from 'notifications';
import preferences from 'Preferences';
import SearchInput from 'search/inputs/SearchInput';
import {Filter, FilterKey, Operator} from 'search/SearchConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import FilesPickerComponent from 'ui-library/FilesPickerComponent';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import * as logging from 'util/Logging';
import removeNulls from 'util/removeNulls';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';
import BrowseFilesRetrievalPanel from './BrowseFilesRetrievalPanel';
import DirectRequestRetrievalPanel from './DirectRequestRetrievalPanel';
import {OnDemandRetrievalKind, PendingRetrieval, RetrievalMode} from './onDemandRetrieval';
import useConnectionRetrievalMode from './useConnectionRetrievalMode';

interface SelectPipelineFilesModalProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  title: string;
  fileIds: Array<string>;
  isMulti: boolean;
  minItems?: number;
  maxItems?: number;
  onSelectFiles: (newFileIds: Array<string>) => void;
  onUploadFiles: (files: Array<File>, selectedFileIds?: Array<string>) => JQueryPromise<void>;
  defaultFilters?: Array<Filter>;
  runApiId: string;
}

interface ConnectionFilesTabProps extends SelectPipelineFilesModalProps {
  selectedFileIds: Array<string>;
  setSelectedFileIds: (selectedFileIds: Array<string>) => void;
  onPendingRetrievalChange: (pendingRetrieval: PendingRetrieval | null) => void;
}

enum TabType {
  DATA_CATALOG = 'data_catalog',
  UPLOAD = 'upload',
  CONNECTION = 'connection',
}

const TAB_TYPE_VALUES = new Set<string>(Object.values(TabType));
const DEFAULT_TAB = TabType.CONNECTION;
const PREFERRED_TAB_PREFERENCE_KEY = 'labAutomation.selectPipelineFilesTab';

function getInitialTab(): TabType {
  const saved = preferences.get(PREFERRED_TAB_PREFERENCE_KEY);
  return typeof saved === 'string' && TAB_TYPE_VALUES.has(saved) ? (saved as TabType) : DEFAULT_TAB;
}

const NOT_ARCHIVED_FILTERS: Array<Filter> = [
  {key: FilterKey.ARCHIVE_PURPOSES, operator: Operator.IS_ONE_OF, value: [NOT_ARCHIVED_PURPOSE_STRING]},
];

const SYNCED_DESCRIPTION = 'Files the connection has already copied to Benchling.';
const ON_DEMAND_DESCRIPTIONS: Record<OnDemandRetrievalKind, string> = {
  [OnDemandRetrievalKind.BROWSE_FILES]:
    'Files on the connection, including ones not yet copied to Benchling. Selected files are retrieved when you click Select.',
  [OnDemandRetrievalKind.DIRECT_REQUEST]:
    "Retrieve data for this run's fields from the connection. The retrieved file is added to your selection.",
};

function getOnDemandUnavailableReason({
  connectionId,
  didDetailsFailToLoad,
  onDemandConnection,
}: Pick<
  ReturnType<typeof useConnectionRetrievalMode>,
  'connectionId' | 'didDetailsFailToLoad' | 'onDemandConnection'
>): string | null {
  if (connectionId == null) {
    return 'Select exactly one connection to retrieve data on demand.';
  }
  if (didDetailsFailToLoad) {
    return "Couldn't load the connection's details. Reselect the connection to try again.";
  }
  if (onDemandConnection == null) {
    return 'This connection does not support retrieving data on demand.';
  }
  return null;
}

function getInitiallySelectedRowIndices(
  connectionFiles: Array<FileListingGraphQL>,
  selectedFileIds: Array<string>
): Set<number> {
  return new Set(
    removeNulls(connectionFiles.map((file, idx) => (selectedFileIds.includes(file.id) ? idx : null)))
  );
}

function ConnectionFilesTab({
  isMulti,
  maxItems,
  selectedFileIds,
  setSelectedFileIds,
  defaultFilters,
  runApiId,
  onPendingRetrievalChange,
}: ConnectionFilesTabProps): JSX.Element {
  const {
    connectionIdsState: [selectedConnectionIds, setSelectedConnectionIds],
    filtersState: [filters, setFilters], // State management for ConnectionFilesTable
  } = useConnectionIdsAndFiltersState({
    initialFilters: defaultFilters ?? getFiltersFromConnectionIds([]),
    initialConnectionIds: null,
  });
  // State management for ConnectionFilesTable
  const [connectionFiles, setConnectionFiles] = useState<Array<FileListingGraphQL>>([]);
  const [selectedRowIndices, setSelectedRowIndices] = useState<Set<number>>(
    getInitiallySelectedRowIndices(connectionFiles, selectedFileIds)
  );

  // Ensure that files that have already been selected and are present in the ConnectionFilesTable are
  // selected when the table loads.
  useEffect(() => {
    setSelectedRowIndices(getInitiallySelectedRowIndices(connectionFiles, selectedFileIds));
  }, [connectionFiles, selectedFileIds]);

  const retrievalMode = useConnectionRetrievalMode(selectedConnectionIds);
  const {isLoadingDetails, mode, setMode} = retrievalMode;
  const onDemandConnection = mode === RetrievalMode.ON_DEMAND ? retrievalMode.onDemandConnection : null;
  const onDemandUnavailableReason = getOnDemandUnavailableReason(retrievalMode);
  // Remounting the table refetches it, so it lists files the connection retrieved on demand.
  const [connectionFilesTableKey, setConnectionFilesTableKey] = useState(0);

  const hasMaxItems = maxItems != null && selectedFileIds.length >= maxItems;

  // Disable "select all" if clicking it would select more connection files than maxItems. Clicking on the
  // checkbox only "selects" if no row indices are selected yet. Otherwise, clicking on it gets rid of all
  // current selections.
  const isSelectAllDisabled =
    selectedRowIndices.size === 0 &&
    maxItems != null &&
    selectedFileIds.length + connectionFiles.length > maxItems;
  // If hasMaxItems === true, then only allow the user to de-select by disabling all *unselected* rows
  const isDisabledMap: Array<boolean> | undefined = hasMaxItems
    ? connectionFiles.map((file, rowIndex) => !selectedRowIndices.has(rowIndex))
    : undefined;

  // Read the selection when a retrieval finishes rather than when it started, since it can take minutes.
  const selectedFileIdsRef = useRef(selectedFileIds);
  selectedFileIdsRef.current = selectedFileIds;
  function handleRetrieveFile(fileId: string): boolean {
    setConnectionFilesTableKey((key) => key + 1);
    if (!isMulti) {
      setSelectedFileIds([fileId]);
      return true;
    }
    const currentFileIds = selectedFileIdsRef.current;
    if (currentFileIds.includes(fileId)) {
      return true;
    }
    // The user may have selected other files while the retrieval was running.
    if (maxItems != null && currentFileIds.length >= maxItems) {
      return false;
    }
    setSelectedFileIds([...currentFileIds, fileId]);
    return true;
  }

  function handleSetRowIndices(newIndices: Set<number>): void {
    // Set the selection state of the ConnectionFilesTable
    const newSelectedRowIndices = new Set(selectedRowIndices);
    for (const rowIndex of newIndices) {
      if (newSelectedRowIndices.has(rowIndex)) {
        // Selected--> unselected
        newSelectedRowIndices.delete(rowIndex);
      } else {
        // Unselected --> selected
        newSelectedRowIndices.add(rowIndex);
      }
    }
    setSelectedRowIndices(newSelectedRowIndices);

    const newSelectedConnectionFileIds = [...newSelectedRowIndices].map(
      (rowIndex) => connectionFiles[rowIndex].id
    );
    if (isMulti) {
      // Inspect selectedFileIds for ids that should not be affected by the change in row indices. Specifically,
      // we don't want to remove files in selectedFileIds that aren't configurable via ConnectionFilesTable,
      // and we also don't want to needlessly change the order of selectedFileIds.
      const allConnectionFileIds = connectionFiles.map(({id}) => id);
      const stableSelectedFileIds = selectedFileIds.filter(
        (id) => !allConnectionFileIds.includes(id) || newSelectedConnectionFileIds.includes(id)
      );
      setSelectedFileIds([
        ...stableSelectedFileIds,
        ...newSelectedConnectionFileIds.filter((id) => !stableSelectedFileIds.includes(id)),
      ]);
    } else {
      assert(
        newSelectedConnectionFileIds.length <= 1,
        'User was able to select multiple connection files for an isMulti=false file variable.'
      );
      setSelectedFileIds(newSelectedConnectionFileIds);
    }
  }

  return (
    <div className={css(styles.connectionTabBody)}>
      <div>
        <FieldLabel label="Connections to filter by" />
        <SearchInput
          autoFocus={false}
          types={[ModelTypeConstants.CONNECTION]}
          additionalFilters={NOT_ARCHIVED_FILTERS}
          selected={selectedConnectionIds}
          onSelect={(connectionId) => {
            if (connectionId == null) {
              return;
            }
            setSelectedConnectionIds([...selectedConnectionIds, connectionId]);
          }}
          onRemove={(connectionId) => {
            if (connectionId == null) {
              return;
            }
            setSelectedConnectionIds(selectedConnectionIds.filter((id) => id !== connectionId));
          }}
          placeholder="Search for connections..."
          isMulti={isMulti}
        />
      </div>
      <Stack spacing={0} align="flex-start">
        <FieldLabel label="Show files" />
        <Tooltip
          label={onDemandUnavailableReason}
          disabled={onDemandUnavailableReason == null || isLoadingDetails}
        >
          <SegmentedControl
            aria-label="Show files"
            data={[
              {label: 'Synced', value: RetrievalMode.SYNCED},
              {
                label: 'On-demand',
                value: RetrievalMode.ON_DEMAND,
                disabled: onDemandUnavailableReason != null,
              },
            ]}
            value={mode}
            onChange={(value: string) => {
              setMode(value as RetrievalMode);
            }}
            size="xs"
          />
        </Tooltip>
        <p className={css(styles.modeDescription)}>
          {onDemandConnection == null
            ? SYNCED_DESCRIPTION
            : ON_DEMAND_DESCRIPTIONS[onDemandConnection.retrievalKind]}
        </p>
      </Stack>
      {isLoadingDetails ? (
        <CenteredHorizontalThrobber />
      ) : onDemandConnection?.retrievalKind === OnDemandRetrievalKind.BROWSE_FILES ? (
        <BrowseFilesRetrievalPanel
          key={onDemandConnection.connectionId}
          connectionId={onDemandConnection.connectionId}
          instrumentServiceDetails={onDemandConnection.instrumentServiceDetails}
          runApiId={runApiId}
          isMulti={isMulti}
          maxSelectableCount={isMulti ? (maxItems == null ? null : maxItems - selectedFileIds.length) : 1}
          onPendingRetrievalChange={onPendingRetrievalChange}
        />
      ) : (
        <>
          {onDemandConnection?.retrievalKind === OnDemandRetrievalKind.DIRECT_REQUEST && (
            <DirectRequestRetrievalPanel
              key={onDemandConnection.connectionId}
              connectionId={onDemandConnection.connectionId}
              instrumentServiceDetails={onDemandConnection.instrumentServiceDetails}
              runApiId={runApiId}
              disabledReason={
                isMulti && hasMaxItems ? 'Remove a selected file before retrieving more data.' : null
              }
              onRetrieveFile={handleRetrieveFile}
              onPendingRetrievalChange={onPendingRetrievalChange}
            />
          )}
          <div>
            <FieldLabel label="Connection files" />
            <ConnectionFilesTable
              key={connectionFilesTableKey}
              isSelectAllDisabled={isSelectAllDisabled}
              isDisabledMap={isDisabledMap}
              hideCheckboxColumn={false}
              allowMultiple={isMulti}
              connectionFiles={connectionFiles}
              setConnectionFiles={setConnectionFiles}
              columns={['File name', 'Status', 'Modified at', 'Created at', 'File path']}
              filters={filters}
              setFilters={setFilters}
              selectedRowIndices={selectedRowIndices}
              onSelectRows={handleSetRowIndices}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default function SelectPipelineFilesModal(props: SelectPipelineFilesModalProps): JSX.Element {
  const {title, fileIds, onSelectFiles, onRequestClose, isMulti, maxItems, onUploadFiles} = props;
  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab);
  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
    preferences.set(PREFERRED_TAB_PREFERENCE_KEY, tab);
  }, []);
  const [selectedFileIds, setSelectedFileIds] = useState<Array<string>>(fileIds);
  // Read the selection when a retrieval finishes rather than when Select was clicked, since the user can keep
  // changing it while the retrieval runs.
  const selectedFileIdsRef = useRef(selectedFileIds);
  selectedFileIdsRef.current = selectedFileIds;
  const [pendingRetrieval, setPendingRetrieval] = useState<PendingRetrieval | null>(null);
  const [isRetrieving, setIsRetrieving] = useState(false);
  // A retrieval can finish after the user cancels the picker, and must not select files then.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fileInputRef = useRef<FilesPickerComponent>(null);

  const handleSubmit = (): void => {
    if (activeTab === TabType.UPLOAD) {
      assert(fileInputRef.current != null, 'Expected non-null fileInputRef.current');
      fileInputRef.current.handleUpload({allComplete: onRequestClose, uploadComplete: noop});
    } else {
      onSelectFiles(selectedFileIds);
      onRequestClose();
    }
  };

  // While a retrieval runs, keep Select bound to it even if the user leaves the connection tab, so the selection
  // can't be submitted twice.
  const isRetrievalPending = isRetrieving || (activeTab === TabType.CONNECTION && pendingRetrieval != null);

  // Retrieves the files picked on the connection tab before selecting them. Uses onClick instead of form
  // submit so the async flow isn't silently dropped by Modal's synchronous onRequestSubmit callback.
  const handleRetrieveAndSubmit = (): void => {
    if (pendingRetrieval == null) {
      return;
    }
    setIsRetrieving(true);
    pendingRetrieval
      .retrieve()
      .then((retrievedFileIds) => {
        if (!isMountedRef.current) {
          return;
        }
        const currentFileIds = selectedFileIdsRef.current;
        // Two sequences with the same name can resolve to the same catalog file.
        const newFileIds = Array.from(
          new Set(isMulti ? [...currentFileIds, ...retrievedFileIds] : retrievedFileIds)
        );
        // The user may have selected other files while the retrieval was running.
        if (maxItems != null && newFileIds.length > maxItems) {
          notifications.error(
            `Maximum file limit of ${maxItems} has been reached. Remove a selected file to add the retrieved data.`
          );
          return;
        }
        onSelectFiles(newFileIds);
        onRequestClose();
      })
      .catch((error: unknown) => {
        if (!isMountedRef.current) {
          return;
        }
        if (error instanceof Error) {
          notifications.error(error.message);
        } else {
          throw error;
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsRetrieving(false);
        }
      });
  };

  const hasMaxItems = maxItems != null && selectedFileIds.length >= maxItems;
  const maxFilesWarningBanner = (
    <InlineBanner
      bannerType={InlineBannerType.WARNING}
      supportingInfo={`Maximum file limit of ${maxItems} has been reached. Files must be removed before any new files can be added.`}
    />
  );

  return (
    <Modal
      extraLarge={activeTab === TabType.CONNECTION}
      large={true}
      enableRedesign={true}
      title={title}
      onRequestClose={onRequestClose}
      onRequestSubmit={isRetrievalPending ? null : handleSubmit}
      buttons={[
        {text: 'Cancel', cancel: true},
        isRetrievalPending
          ? {
              text: isRetrieving ? 'Retrieving...' : 'Select',
              style: ButtonStyle.PRIMARY,
              onClick: handleRetrieveAndSubmit,
              disabled: isRetrieving,
            }
          : {
              text: 'Select',
              submit: true,
            },
      ]}
    >
      <TabContainer<TabType> activeTab={activeTab} onRequestTabChange={handleTabChange}>
        <Tab tabKey={TabType.CONNECTION} title="Select from connection">
          <div className={css(styles.tabBody)}>
            {hasMaxItems && maxFilesWarningBanner}
            <ConnectionFilesTab
              {...props}
              selectedFileIds={selectedFileIds}
              setSelectedFileIds={setSelectedFileIds}
              onPendingRetrievalChange={setPendingRetrieval}
            />
          </div>
        </Tab>
        <Tab tabKey={TabType.DATA_CATALOG} title="Select from data catalog">
          <div className={css(styles.tabBody)}>
            {hasMaxItems && maxFilesWarningBanner}
            <SearchInput
              autoFocus={false}
              disableTyping={hasMaxItems || (!isMulti && selectedFileIds.length === 1)}
              maxItems={maxItems}
              types={[ModelTypeConstants.FILE]}
              selected={selectedFileIds}
              onSelect={(fileId) => {
                if (fileId == null) {
                  return;
                }
                if (isMulti) {
                  // Do nothing if the number of selected files is already >= maxItems, and log an error so
                  // that we know that something is wrong with our UI.
                  if (hasMaxItems) {
                    logging.error(
                      'User was able to select files past maxItems limit from "Select from data catalog" tab.'
                    );
                    return;
                  }
                  setSelectedFileIds([...selectedFileIds, fileId]);
                } else {
                  setSelectedFileIds([fileId]);
                }
              }}
              onRemove={(fileId) => {
                if (fileId == null) {
                  return;
                }
                setSelectedFileIds(selectedFileIds.filter((id) => id !== fileId));
              }}
              placeholder={hasMaxItems ? '' : 'Search for files...'}
              isMulti={isMulti}
            />
          </div>
        </Tab>
        <Tab tabKey={TabType.UPLOAD} title={`Upload file${isMulti ? 's' : ''}`}>
          <div className={css(styles.tabBody)}>
            {hasMaxItems && maxFilesWarningBanner}
            <FilesPickerComponent
              isDisabled={hasMaxItems}
              multiple={isMulti}
              // Calculate the max files that a user can select for upload based on how many files have already
              // been selected.
              maxFiles={maxItems == null ? undefined : maxItems - selectedFileIds.length}
              upload={(files: Array<File>) => {
                // Do nothing if the number of selected files is already >= maxItems, and log an error so
                // that we know that something is wrong with our UI.
                if (hasMaxItems) {
                  logging.error('User was able to select files past maxItems limit from "Upload files" tab.');
                  return $.when();
                }
                return onUploadFiles(
                  files,
                  // If isMulti=true, then the new file(s) will be appended to the list of already selected
                  // files. Otherwise, the existing file will be overwritten by the new file.
                  isMulti ? selectedFileIds : []
                );
              }}
              bulkUpload={true}
              ref={fileInputRef}
            />
          </div>
        </Tab>
      </TabContainer>
    </Modal>
  );
}

const styles = StyleSheet.create({
  tabBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  connectionTabBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  modeDescription: {
    ...FONT_PROXIMA_13,
    color: GRAY_SEMIDARK,
    margin: '4px 0 0',
  },
});
